import React from 'react';
import { Camera, Sparkles, Shield } from 'lucide-react';
import Reveal from './motion/Reveal';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: Camera,
      title: 'Snap Photos',
      description: 'Walk through your home with your smartphone. Take photos of your belongings room by room.'
    },
    {
      number: '02',
      icon: Sparkles,
      title: 'AI Does the Work',
      description: 'Our AI automatically identifies items, determines make/model/brand, and calculates replacement values.'
    },
    {
      number: '03',
      icon: Shield,
      title: 'You\'re Protected',
      description: 'Export your complete, documented inventory. When you need to claim, you have undisputable proof of ownership.'
    }
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mb-20">
            <p className="eyebrow text-cyan-600 mb-5">How it works</p>
            <h2 className="text-4xl lg:text-6xl font-bold text-ink leading-tight">
              Simple enough to actually do
            </h2>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              Creating a comprehensive contents inventory takes minutes, not hours. Here's how it works:
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-12 relative">
          <div className="hidden md:block absolute top-10 left-0 right-0 h-px bg-gradient-to-r from-cyan-300 via-emerald-300 to-cyan-300"></div>

          {steps.map((step, index) => (
            <Reveal key={index} delay={index * 0.12}>
              <div className="relative group">
                <div className="flex items-center gap-5 mb-8">
                  <div className="w-20 h-20 bg-ink rounded-2xl flex items-center justify-center relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3 shadow-lg">
                    <step.icon className="w-9 h-9 text-cyan-400" />
                  </div>
                  <span className="text-7xl font-bold text-slate-100 group-hover:text-cyan-100 transition-colors duration-500 select-none">
                    {step.number}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-ink mb-3">{step.title}</h3>
                <p className="text-slate-600 text-lg leading-relaxed">{step.description}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-24 bg-paper rounded-3xl p-10 lg:p-14 border rule-light text-center">
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="w-16 h-16 bg-ink rounded-2xl flex items-center justify-center mx-auto">
                <Sparkles className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-3xl font-bold text-ink">
                From hours to minutes
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Traditional contents inventories are tedious spreadsheet exercises that take hours. 
                <a href="https://verifaeye.com.au" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700 font-semibold"> VerifAEye's</a> AI-powered approach means you can document your entire home in the time it takes to watch a TV show.
              </p>
              <div className="flex flex-wrap justify-center gap-10 pt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-ink">&lt; 30<span className="text-cyan-600">min</span></div>
                  <div className="text-slate-500 mt-1 text-sm">Average completion time</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-ink">100<span className="text-emerald-600">%</span></div>
                  <div className="text-slate-500 mt-1 text-sm">Metadata verified</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-ink">AI<span className="text-cyan-600">-powered</span></div>
                  <div className="text-slate-500 mt-1 text-sm">Accuracy</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default HowItWorks;
